import { processStopProxySession } from 'app/blocks/analytics/analytics';
import { updateProxySession } from 'app/blocks/analytics/proxySession';
import emitter from 'app/blocks/common/events/emitter';
import Events from 'app/blocks/common/events/Events';
import storage from 'app/blocks/common/storage';
import { isMock, isNonProd } from 'app/blocks/middleware/config';
import { getUser, setUser } from 'app/blocks/middleware/user';
import { appendInvitationTokenToParams, removeInvitationToken } from './register';
import { GET, POST } from './utils';
import { SalesforceWindow } from '../HelpWidget/useHelpWidget';

const urls = {
    connectRedirectBaseUrl: null,
};

export function setAuthenticated(value: boolean) {
    if (storage.get('authenticated') === value) {
        return;
    }

    storage.set('authenticated', value);
}

export async function refreshUser(): Promise<User> {
    const user = await GET('/v2/loggedUserData');
    setUser(user);
    emitter.emit(Events.USER_CHANGED, user);
    setAuthenticated(user.loggedIn);

    if (user?.isAdminMode && user?.imitateBy) {
        updateProxySession(user?.imitateBy?.connectSessionId);
    }

    return user;
}

export async function login(originParams) {
    const result = await POST('/authenticate', appendInvitationTokenToParams(originParams));
    removeInvitationToken();
    await refreshUser();

    return result;
}

export async function logout(stayOnPage = false) {
    const salesforceWindow = window as unknown as SalesforceWindow;

    try {
        await GET('/v2/logout');
    } finally {
        const user = getUser();

        const isImitation = user?.loggedIn && user.imitateBy && user?.isAdminMode;
        if (isImitation) {
            await processStopProxySession();
        }
        setUser({ loggedIn: false });
        setAuthenticated(false);

        if (salesforceWindow.embeddedservice_bootstrap) {
            salesforceWindow.logoutAndClearMIAWSession();
            console.log('✅ HelpWidget: Chat logout successful');
        }

        if (stayOnPage) {
            emitter.emit(Events.USER_CHANGED, { loggedIn: false });
        } else {
            let env = 'nonprod';
            if (isMock()) {
                env = 'mock';
            } else if (!isNonProd()) {
                env = 'prod';
            }

            window.location.href = `/logout.html?env=${env}&adm=${isImitation}`;
        }
    }
}

export async function getConnectRedirectUrl() {
    if (!urls.connectRedirectBaseUrl) {
        urls.connectRedirectBaseUrl = await GET('/connect/redirect-url');
    }
    return urls.connectRedirectBaseUrl;
}

async function getConnectUrl() {
    return await GET('/connect/url');
}

export async function authenticateASLogin(originParams) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { action, invitationToken, ...other } = originParams;
    const params = invitationToken
        ? { ...other, invitationToken: decodeURIComponent(decodeURIComponent(invitationToken)) }
        : other;
    const result = await POST('/authenticate/connect', params);
    removeInvitationToken();
    await refreshUser();
    return result;
}

const isURL = candidate => {
    try {
        const url = new URL(candidate);
        return !!url.hostname;
    } catch (e) {
        return false;
    }
};

function addLoginActionAndInvitationToken(href: string, invitationToken?: string) {
    const url = new URL(href);
    url.searchParams.set('action', 'login');
    if (invitationToken) {
        url.searchParams.set('invitationToken', invitationToken);
    }
    return url.href;
}

export function addRedirectUriParam(url: URL, relativePath: string, redirectBaseUrl: string, invitationToken?: string) {
    const pathname = !!relativePath && relativePath !== '/' ? relativePath : '/index.html/dashboard';
    const redirect_uri = `${redirectBaseUrl}${pathname}`;
    url.searchParams.set('redirect_uri', addLoginActionAndInvitationToken(redirect_uri, invitationToken));
}

export async function getUiConnectUrl(relativePath?: string, isSkipRedirectURI?: boolean) {
    const silentAuthUrl = await getConnectUrl();
    const result = new URL(silentAuthUrl?.replace('&prompt=none', ''));
    if (!isSkipRedirectURI) {
        const redirectBaseUrl = await getConnectRedirectUrl();
        addRedirectUriParam(result, relativePath, redirectBaseUrl);
    }
    return result.href;
}

export async function getUrlWithRedirectURI(url: string, redirectPath?: string) {
    const redirectBaseUrl = await getConnectRedirectUrl();
    const result = isURL(url) ? new URL(url) : new URL(url, redirectBaseUrl);
    addRedirectUriParam(result, redirectPath, redirectBaseUrl);
    return result;
}

export function securityKey(url) {
    const randomString = isMock() ? 'MY_STATE' : (Math.random() + 1).toString(36).substring(7);
    localStorage.setItem('STATE_ID', randomString);
    return url.replace(/state=MY_STATE/, `state=${randomString}`);
}

storage.watch('authenticated', newValue => {
    setTimeout(() => {
        console.debug(newValue);
        refreshUser();
    }, 0);
});
